import { botttsNeutral } from "@dicebear/collection";
import { createAvatar } from "@dicebear/core";
import { type FC, useMemo } from "react";

import type { InstanceStatus } from "./blackboardModels";

import { ActivePulseIndicator } from "../common/ActivePulseIndicator";
import { applyAvatarAnimations } from "./avatarAnimations";

// Theme-aligned palette (emerald, violet, amber, rose, slate) — DiceBear picks
// One deterministically from the seed so the same actor always gets the same
// Background color. Hex values mirror tailwind.config arti-* tokens.
const THEME_BACKGROUND_COLORS = [
  "10b981", // Arti-primary (emerald-500)
  "8b5cf6", // Violet accent (sliders / links)
  "f59e0b", // Arti-warning (amber-500)
  "f43f5e", // Arti-danger (rose-500)
  "475569", // Arti-secondary (slate-600)
];

// Curated eye/mouth subsets — dropped anything overtly cute (hearts, happy,
// Smile01/02) or too cartoonish (bulging, bite) to stay brutalist / technical.
const AESTHETIC_EYES = [
  "eva",
  "frame1",
  "frame2",
  "glow",
  "robocop",
  "round",
  "roundFrame01",
  "sensor",
  "shade01",
] as const;

const AESTHETIC_MOUTHS = [
  "diagram",
  "grill01",
  "grill02",
  "grill03",
  "square01",
  "square02",
] as const;

interface InstanceAvatarProps {
  seed: string;
  /** "md" = 40×40px (card), "sm" = 24×24px (chat bubble). Defaults to "md". */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** When provided, renders a status indicator dot. */
  status?: InstanceStatus;
  /**
   * Inject SMIL animations: per-eye blinks (with occasional one-eye-only
   * winks for two-eye types), idle mouth motion (EKG scroll, equalizer
   * bounce, smile breath, etc.), and visor shimmer. Off by default — turn
   * on for actor-instance contexts; leave off for template hero cards and
   * spawn previews where the avatar should read as static iconography.
   */
  animated?: boolean;
}

type InstanceAvatarSize = NonNullable<InstanceAvatarProps["size"]>;

const PIXEL_SIZE_BY_AVATAR_SIZE: Record<InstanceAvatarSize, number> = {
  lg: 64,
  md: 40,
  sm: 24,
  xl: 96,
  xs: 16,
};

const SIZE_CLASS_BY_AVATAR_SIZE: Record<InstanceAvatarSize, string> = {
  lg: "h-16 w-16",
  md: "h-10 w-10",
  sm: "h-6 w-6",
  xl: "h-24 w-24",
  xs: "h-4 w-4",
};

const getPixelSize = (size: InstanceAvatarSize) =>
  PIXEL_SIZE_BY_AVATAR_SIZE[size];

const getSizeClass = (size: InstanceAvatarSize) =>
  SIZE_CLASS_BY_AVATAR_SIZE[size];

/**
 * Returns the hex color (with `#` prefix) DiceBear deterministically picked
 * for this seed's background, so callers can match avatar to decorative UI.
 *
 * DiceBear emits a `#fff` placeholder rect before the real background, so
 * we scan every rect and return the first fill that matches the theme
 * palette. Falls back to the first palette entry if nothing matches.
 */
export function getAvatarBackgroundColor(seed: string): string {
  const svg = createAvatar(botttsNeutral, {
    backgroundColor: THEME_BACKGROUND_COLORS,
    backgroundType: ["solid"],
    eyes: [...AESTHETIC_EYES],
    mouth: [...AESTHETIC_MOUTHS],
    radius: 0,
    seed,
    size: 40,
  }).toString();
  const palette = new Set(
    THEME_BACKGROUND_COLORS.map((c) => `#${c.toLowerCase()}`),
  );
  for (const match of svg.matchAll(/<rect[^>]*fill="([^"]+)"/g)) {
    const fill = match[1].toLowerCase();
    if (palette.has(fill)) {
      return fill;
    }
  }
  return `#${THEME_BACKGROUND_COLORS[0]}`;
}

export const InstanceAvatar: FC<InstanceAvatarProps> = ({
  seed,
  size = "md",
  status,
  animated = false,
}) => {
  const pixelSize = getPixelSize(size);
  const svg = useMemo(() => {
    const raw = createAvatar(botttsNeutral, {
      backgroundColor: THEME_BACKGROUND_COLORS,
      backgroundType: ["solid"],
      eyes: [...AESTHETIC_EYES],
      mouth: [...AESTHETIC_MOUTHS],
      radius: 0,
      seed,
      size: pixelSize,
    }).toString();
    return animated ? applyAvatarAnimations(raw, seed) : raw;
  }, [seed, pixelSize, animated]);

  const sizeClass = getSizeClass(size);

  return (
    <div className="relative flex-shrink-0">
      {/* Decorative: the DiceBear SVG carries verbose Bottts/Pablo Stanley
          license metadata; aria-hidden keeps it out of the screen-reader tree
          (the actor's display name is always rendered as adjacent text). */}
      <div
        aria-hidden
        className={`${sizeClass} overflow-hidden`}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {status !== undefined && size !== "sm" && <StatusDot status={status} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// StatusDot
// ---------------------------------------------------------------------------

const StatusDot: FC<{ status: InstanceStatus }> = ({ status }) => {
  if (status === "running") {
    return (
      <span className="absolute -bottom-0.5 -right-0.5 border-2 border-white dark:border-slate-900">
        <ActivePulseIndicator
          size="xs"
          colorClassName="bg-emerald-500"
          radarClassName="border-emerald-500/50"
        />
      </span>
    );
  }

  return (
    <span
      className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 border-2 border-white dark:border-slate-900 ${status === "waiting" ? "bg-amber-400" : "bg-rose-500"}`}
    />
  );
};
